import {
  CARGO_PORT_OPERATIONS,
  CARGO_UNITS,
  CHECKLIST_STATUSES,
  currentCharterParty,
  DEPARTURE_PURPOSES,
  deriveEuRoute,
  EMPLOYMENT_STATUSES,
  EU_ROUTES,
  LOAD_CONDITIONS,
  mrvReviewStatus,
  type PassageReview,
  passageReview,
  VOYAGE_ACTIONS,
  type VoyageAction,
} from '@felix/domain';
import { ActionIcon, Button, Checkbox, MultiSelect, Radio, Select } from '@mantine/core';
import {
  IconAlertTriangle,
  IconAnchor,
  IconBox,
  IconClipboardCheck,
  IconFlag,
  IconInfoCircle,
  IconLink,
  IconPlus,
  IconRoute,
  IconShip,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect } from 'react';
import { useController, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ltUtcText } from '../../inputs/lt-utc.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './DepartureSections.module.css';
import {
  NoteField,
  NumberField,
  PortField,
  SelectField,
  TextField,
  useFieldError,
} from './fields.js';
import le from './LegEventScreen.module.css';
import noon from './NoonReportScreen.module.css';
import { Group, LocalDateTimeField } from './NoonSections.js';
import { formatLatitude, formatLongitude } from './noon-report.js';
import { useNumber } from './numbers.js';
import rs from './ReportSections.module.css';
import type { CharterPartyItem, Voyage, VoyageReport, VoyageReportContext } from './types.js';

/**
 * Kalkış ve varış raporlarının bölümleri (ADR 0058; ortağın `departure-report.jpeg`, `g2.jpeg`,
 * `arrival-report.jpeg`, `g4.jpeg` ve tel kafesi): pilot ve römorkör, sonraki liman, gemi
 * durumu, yük, sınıflandırma, sefer kararı, son kontrol, Port Call, NOR, halat; EOSP'ta seyir
 * incelemesi, Berthing'de EOSP referansı. Alanlar `data` altında; doğrulama `parseVoyageReport`.
 */

const useK = () => {
  const { t } = useTranslation();
  return (key: string, options?: Record<string, unknown>) =>
    t(`voyage.sections.${key}`, options ?? {});
};

const blank = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value)
    ? value.every(blank)
    : typeof value === 'object' && Object.values(value).every(blank));

/** Salt okunur raporda boş bölüm gösterilmez. */
function useHidden(names: readonly string[], readOnly: boolean): boolean {
  const values = useWatch({ name: names as string[] }) as unknown[];
  return readOnly && values.every(blank);
}

const pairClass = [noon.pair, le.fieldPair].join(' ');

/** Boolean alan (N/A gibi): işaret kutusu. */
function CheckField({ name, label, readOnly }: { name: string; label: string; readOnly: boolean }) {
  const { field } = useController({ name });
  const error = useFieldError(name);
  return (
    <Checkbox
      className={classes.check}
      label={label}
      checked={field.value === true}
      disabled={readOnly}
      onChange={(event) => field.onChange(event.currentTarget.checked)}
      error={error ?? null}
    />
  );
}

/* ---------- Pilot, Cast Off ve römorkör sayısı (Departure; ADR 0058 §1) ---------- */

export function PilotSection({ readOnly }: { readOnly: boolean }) {
  const k = useK();
  const { setValue, getValues, clearErrors } = useFormContext();
  const notUsed = useWatch({ name: 'data.pilotNotUsed' }) === true;
  const tugs = (useWatch({ name: 'data.tugs' }) ?? []) as unknown[];
  // Pilot kullanılmadıysa zamanlar boşalır (N/A; tel kafes Ç3)
  useEffect(() => {
    if (readOnly || !notUsed) return;
    // Yalnız dolu zaman boşalır: kaydedilmiş taslakta boş alana `null` yazılmaz (değişiklik yokken
    // "kaydedilmemiş" sayılmasın). Zamanların bayat "Required" hatası her durumda kalkar
    const names = ['data.pilotOnBoard', 'data.pilotOff'] as const;
    for (const name of names) {
      if (getValues(name) !== null && getValues(name) !== undefined) setValue(name, null);
    }
    clearErrors([...names]);
  }, [readOnly, notUsed, setValue, getValues, clearErrors]);
  return (
    <Group title={k('pilot.title')} icon={<IconFlag size={18} />} className={le.wide}>
      <p className={noon.hint}>{k('pilot.castOffNote')}</p>
      <CheckField name="data.pilotNotUsed" label={k('pilot.notUsed')} readOnly={readOnly} />
      {notUsed ? null : (
        <div className={pairClass}>
          <LocalDateTimeField
            name="data.pilotOnBoard"
            label={k('pilot.onBoard')}
            required
            readOnly={readOnly}
          />
          <LocalDateTimeField
            name="data.pilotOff"
            label={k('pilot.off')}
            required
            readOnly={readOnly}
          />
        </div>
      )}
      <div className={pairClass}>
        <NumberField
          name="data.tugsUsed"
          label={k('pilot.tugsUsed')}
          decimals={0}
          required
          readOnly={readOnly || tugs.length > 0}
        />
      </div>
      {tugs.length > 0 && !readOnly ? <p className={noon.hint}>{k('pilot.tugsFromRows')}</p> : null}
    </Group>
  );
}

/* ---------- Sonraki liman ---------- */

export function NextPortSection({
  kind,
  readOnly,
}: {
  kind: 'departure' | 'arrival';
  readOnly: boolean;
}) {
  const k = useK();
  const departure = kind === 'departure';
  const hidden = useHidden(['data.nextPort', 'data.nextPortUnknownReason'], readOnly);
  if (hidden) return null;
  return (
    <Group title={k('nextPort.title')} icon={<IconRoute size={18} />} className={le.wide}>
      <div className={pairClass}>
        <PortField
          name="data.nextPort"
          codeName="data.nextPortCode"
          label={k('nextPort.port')}
          required={departure}
          readOnly={readOnly}
        />
        {departure ? (
          <NumberField
            name="data.distanceToNextPortNm"
            label={k('nextPort.distance')}
            unit="NM"
            decimals={1}
            readOnly={readOnly}
          />
        ) : null}
        {departure ? (
          <LocalDateTimeField
            name="data.nextPortEta"
            label={k('nextPort.eta')}
            readOnly={readOnly}
          />
        ) : null}
        {departure ? (
          <TextField
            name="data.nextPortUnknownReason"
            label={k('nextPort.unknownReason')}
            readOnly={readOnly}
          />
        ) : null}
      </div>
      {departure && !readOnly ? <p className={noon.hint}>{k('nextPort.note')}</p> : null}
    </Group>
  );
}

/* ---------- Gemi durumu (yükleme): yük durumu, draft, air draft, deplasman ---------- */

export function ShipLoadSection({
  kind,
  readOnly,
}: {
  kind: 'departure' | 'arrival';
  readOnly: boolean;
}) {
  const k = useK();
  const { t } = useTranslation();
  const departure = kind === 'departure';
  const hidden = useHidden(
    ['data.loadCondition', 'data.draft', 'data.airDraftM', 'data.displacementMt'],
    readOnly,
  );
  if (hidden) return null;
  return (
    <Group title={k('load.title')} icon={<IconShip size={18} />} className={le.wide}>
      <div className={pairClass}>
        <SelectField
          name="data.loadCondition"
          label={k('load.condition')}
          readOnly={readOnly}
          options={LOAD_CONDITIONS.map((value) => ({
            value,
            label: t(`voyage.event.options.loadConditions.${value}`),
          }))}
        />
        <NumberField
          name="data.airDraftM"
          label={k('load.airDraft')}
          unit="m"
          decimals={2}
          readOnly={readOnly}
        />
        <NumberField
          name="data.draft.fwdM"
          label={k('load.draftFwd')}
          unit="m"
          decimals={2}
          required={departure}
          readOnly={readOnly}
          fixed
        />
        <NumberField
          name="data.draft.aftM"
          label={k('load.draftAft')}
          unit="m"
          decimals={2}
          required={departure}
          readOnly={readOnly}
          fixed
        />
        {departure ? (
          <NumberField
            name="data.displacementMt"
            label={k('load.displacement')}
            unit="MT"
            decimals={0}
            readOnly={readOnly}
          />
        ) : null}
      </div>
    </Group>
  );
}

/* ---------- Yük ---------- */

export function CargoSection({
  kind,
  readOnly,
}: {
  kind: 'departure' | 'arrival';
  readOnly: boolean;
}) {
  const k = useK();
  const departure = kind === 'departure';
  const { fields, append, remove } = useFieldArray({ name: 'data.cargo.lines' });
  const operation = useWatch({ name: 'data.cargo.operation' }) as string | null | undefined;
  const hidden = useHidden(['data.cargo'], readOnly);
  if (hidden) return null;
  const showLoaded = operation === 'loading' || operation === 'both';
  const showDischarged = operation === 'discharging' || operation === 'both';
  const units = CARGO_UNITS.map((value) => ({ value, label: k(`cargo.units.${value}`) }));
  return (
    <Group title={k('cargo.title')} icon={<IconBox size={18} />} className={le.wide}>
      <div className={pairClass}>
        <SelectField
          name="data.cargo.operation"
          label={departure ? k('cargo.operationDone') : k('cargo.operationPlanned')}
          required={departure}
          readOnly={readOnly}
          options={CARGO_PORT_OPERATIONS.map((value) => ({
            value,
            label: k(`cargo.operations.${value}`),
          }))}
        />
      </div>
      {operation && operation !== 'none' ? (
        <div className={rs.rows}>
          {fields.map((row, index) => (
            <fieldset
              key={row.id}
              className={classes.cargoRow}
              aria-label={k('cargo.row', { no: index + 1 })}
            >
              <span className={rs.rowNo} aria-hidden>
                {index + 1}
              </span>
              <TextField
                name={`data.cargo.lines.${index}.cargoType`}
                label={k('cargo.type')}
                required
                readOnly={readOnly}
              />
              <SelectField
                name={`data.cargo.lines.${index}.unit`}
                label={k('cargo.unit')}
                required
                readOnly={readOnly}
                options={units}
              />
              {readOnly ? (
                <span />
              ) : (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  className={rs.remove}
                  aria-label={k('cargo.remove', { no: index + 1 })}
                  onClick={() => remove(index)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              )}
              {showLoaded ? (
                <NumberField
                  name={`data.cargo.lines.${index}.loadedQty`}
                  label={departure ? k('cargo.loaded') : k('cargo.toLoad')}
                  required={departure}
                  decimals={2}
                  readOnly={readOnly}
                />
              ) : null}
              {showDischarged ? (
                <NumberField
                  name={`data.cargo.lines.${index}.dischargedQty`}
                  label={departure ? k('cargo.discharged') : k('cargo.toDischarge')}
                  required={departure}
                  decimals={2}
                  readOnly={readOnly}
                />
              ) : null}
            </fieldset>
          ))}
          {!readOnly && fields.length < 10 ? (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              className={le.addRow}
              onClick={() =>
                append({ cargoType: '', loadedQty: null, dischargedQty: null, unit: 'MT' })
              }
            >
              {k('cargo.add')}
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className={pairClass}>
        <NumberField
          name="data.cargo.onBoardQty"
          label={k('cargo.onBoard')}
          decimals={2}
          readOnly={readOnly}
        />
        <SelectField
          name="data.cargo.onBoardUnit"
          label={k('cargo.onBoardUnit')}
          readOnly={readOnly}
          options={units}
        />
      </div>
      <NoteField
        name="data.cargo.remarks"
        label={k('cargo.remarks')}
        readOnly={readOnly}
        minRows={2}
      />
      {departure && !readOnly ? <p className={noon.hint}>{k('cargo.note')}</p> : null}
    </Group>
  );
}

/* ---------- Sınıflandırma ve AB rota taraması ---------- */

export function ClassificationSection({
  readOnly,
  charterParties = null,
}: {
  readOnly: boolean;
  /**
   * Geminin CP register'ı (gemide salt okunur; ADR 0084): "Kiralı" kalkışı kapsayan CP yoksa not
   * (ADR 0157 §7). `null` okunmadı: not çıkmaz.
   */
  charterParties?: readonly CharterPartyItem[] | null;
}) {
  const k = useK();
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const [employment, reportedAt] = useWatch({
    name: ['data.classification.employment', 'reportedAt'],
  }) as [string | null, string | null];
  // "Kiralı" yalnız sınıflamadır: CP kaydını ofis açar, kira analizi CP dönemiyle başlar (S15)
  const noCp =
    employment === 'chartered' &&
    charterParties !== null &&
    !!reportedAt &&
    currentCharterParty(charterParties, reportedAt) === null;
  const [purpose, route, manual, portCode, nextPortCode] = useWatch({
    name: [
      'data.classification.purpose',
      'data.classification.euRoute',
      'data.classification.euRouteManual',
      'data.portCode',
      'data.nextPortCode',
    ],
  }) as [string | null, string | null, boolean | null, string | null, string | null];
  // Rota limanlardan türetilir; elle düzeltilmişse dokunulmaz (tel kafes "derived + override")
  const derived = deriveEuRoute(portCode, nextPortCode);
  useEffect(() => {
    if (readOnly || manual === true || route === derived) return;
    // Türetilen değer kullanıcının değişikliği sayılmaz (yeni formda "kaydedilmemiş" uyarısı çıkmaz)
    setValue('data.classification.euRoute', derived);
  }, [readOnly, manual, route, derived, setValue]);
  const { field: routeField } = useController({ name: 'data.classification.euRoute' });
  const mrv = mrvReviewStatus((route as never) ?? null);
  return (
    <Group title={k('classification.title')} icon={<IconFlag size={18} />} className={le.wide}>
      <div className={pairClass}>
        <SelectField
          name="data.classification.employment"
          label={k('classification.employment')}
          required
          readOnly={readOnly}
          options={EMPLOYMENT_STATUSES.map((value) => ({
            value,
            label: k(`classification.employments.${value}`),
          }))}
        />
        <SelectField
          name="data.classification.purpose"
          label={k('classification.purpose')}
          required
          readOnly={readOnly}
          options={DEPARTURE_PURPOSES.map((value) => ({
            value,
            label: k(`classification.purposes.${value}`),
          }))}
        />
        {purpose === 'other' ? (
          <TextField
            name="data.classification.purposeOther"
            label={k('classification.purposeOther')}
            required
            readOnly={readOnly}
          />
        ) : null}
        <TextField
          name="data.classification.charterReference"
          label={k('classification.charterReference')}
          readOnly={readOnly}
        />
        <Select
          label={k('classification.euRoute')}
          data={EU_ROUTES.map((value) => ({ value, label: k(`classification.euRoutes.${value}`) }))}
          value={typeof routeField.value === 'string' ? routeField.value : null}
          readOnly={readOnly}
          allowDeselect={false}
          onChange={(value) => {
            routeField.onChange(value);
            setValue('data.classification.euRouteManual', value !== derived, { shouldDirty: true });
          }}
          {...(readOnly ? { rightSection: null } : {})}
          classNames={{
            input: [noon.input, readOnly ? noon.locked : ''].join(' '),
            label: noon.label ?? '',
          }}
        />
        <div className={classes.mrv}>
          <span className={noon.label}>{k('classification.mrv')}</span>
          <StatusBadge tone={mrv === 'not_assessed' ? 'neutral' : 'warning'} variant="dot">
            {k(`classification.mrvStatuses.${mrv}`)}
          </StatusBadge>
        </div>
      </div>
      <p className={noon.hint}>
        {manual ? k('classification.euRouteManual') : k('classification.euRouteDerived')}
      </p>
      {noCp ? (
        <div className={noon.notice} data-tone="info" role="status">
          <IconInfoCircle size={16} aria-hidden />
          <span>{t('voyage.context.charter.noCp')}</span>
        </div>
      ) : null}
    </Group>
  );
}

/* ---------- Sefer kararı (Link existing / Create new / No voyage yet) ---------- */

export function VoyageDecisionSection({
  readOnly,
  voyages,
}: {
  readOnly: boolean;
  /** Bağlanabilecek seferler (sürmekte ya da planlanan). */
  voyages: readonly Pick<Voyage, 'id' | 'voyageNo' | 'status'>[];
}) {
  const k = useK();
  const { field: action } = useController({ name: 'data.voyageAction' });
  const { field: voyageId } = useController({ name: 'voyageId' });
  const error = useFieldError('data.voyageAction');
  const value = typeof action.value === 'string' ? (action.value as VoyageAction) : null;
  const linked = voyages.find((v) => v.id === voyageId.value) ?? null;
  return (
    <Group title={k('decision.title')} icon={<IconLink size={18} />} className={le.wide}>
      <Radio.Group
        value={value}
        onChange={(next) => {
          if (readOnly) return;
          action.onChange(next);
          // Seferli kalkış "link"; diğerleri sefersiz (ADR 0058 §3)
          voyageId.onChange(next === 'link' ? (voyageId.value ?? voyages[0]?.id ?? null) : null);
        }}
        label={k('decision.question')}
        withAsterisk={!readOnly}
        error={error ?? null}
        classNames={{ label: noon.label ?? '' }}
      >
        <div className={rs.nextOptions}>
          {VOYAGE_ACTIONS.map((option) => (
            <Radio.Card
              key={option}
              value={option}
              disabled={readOnly ? value !== option : option === 'link' && voyages.length === 0}
              className={rs.nextCard}
              data-checked={value === option || undefined}
              aria-label={k(`decision.actions.${option}`)}
            >
              <Radio.Indicator size="xs" />
              <span className={rs.nextText}>
                <strong>{k(`decision.actions.${option}`)}</strong>
                <small>{k(`decision.hints.${option}`)}</small>
              </span>
            </Radio.Card>
          ))}
        </div>
      </Radio.Group>
      {value === 'link' ? (
        <Select
          label={k('decision.voyage')}
          data={voyages.map((v) => ({ value: v.id, label: v.voyageNo }))}
          value={typeof voyageId.value === 'string' ? voyageId.value : null}
          readOnly={readOnly}
          allowDeselect={false}
          onChange={(next) => next && voyageId.onChange(next)}
          {...(readOnly ? { rightSection: null } : {})}
          {...(linked ? {} : { placeholder: k('decision.noVoyage') })}
          classNames={{
            input: [noon.input, readOnly ? noon.locked : ''].join(' '),
            label: noon.label ?? '',
          }}
        />
      ) : null}
      {value === 'none' ? (
        <TextField
          name="data.noVoyageReason"
          label={k('decision.reason')}
          required
          readOnly={readOnly}
        />
      ) : null}
      <p className={noon.hint}>{k('decision.note')}</p>
    </Group>
  );
}

/* ---------- Son kontrol: checklist ve açık arızalar ---------- */

export function FinalCheckSection({
  readOnly,
  context,
}: {
  readOnly: boolean;
  context: VoyageReportContext | null;
}) {
  const k = useK();
  const { setValue } = useFormContext();
  const status = useWatch({ name: 'data.checklist.status' }) as string | null | undefined;
  const recordId = useWatch({ name: 'data.checklist.formRecordId' }) as string | null | undefined;
  const recordLabel = useWatch({ name: 'data.checklist.formRecordLabel' }) as string | null;
  const items = (useWatch({ name: 'data.openDefects.items' }) ?? []) as {
    id: string;
    label: string;
  }[];
  const hidden = useHidden(['data.checklist', 'data.openDefects'], readOnly);
  if (hidden) return null;
  const records = context?.formRecords ?? [];
  const recordOptions = records.map((r) => ({ value: r.id, label: `${r.recordNo} · ${r.title}` }));
  if (recordId && !recordOptions.some((o) => o.value === recordId)) {
    recordOptions.unshift({ value: recordId, label: recordLabel ?? recordId });
  }
  const defects = context?.openDefects ?? [];
  const defectOptions = defects.map((d) => ({
    value: d.id,
    label: `${d.defectNo} · ${d.description}`.slice(0, 120),
  }));
  for (const item of items) {
    if (!defectOptions.some((o) => o.value === item.id)) {
      defectOptions.push({ value: item.id, label: item.label });
    }
  }
  const critical = defects.filter((d) => d.critical);
  return (
    <Group
      title={k('finalCheck.title')}
      icon={<IconClipboardCheck size={18} />}
      className={le.wide}
    >
      {critical.length > 0 && !readOnly ? (
        <div className={noon.notice} data-tone="warning" role="status">
          <IconAlertTriangle size={16} aria-hidden />
          <span>{k('finalCheck.criticalOpen', { count: critical.length })}</span>
        </div>
      ) : null}
      <div className={pairClass}>
        <SelectField
          name="data.checklist.status"
          label={k('finalCheck.checklist')}
          readOnly={readOnly}
          options={CHECKLIST_STATUSES.map((value) => ({
            value,
            label: k(`finalCheck.statuses.${value}`),
          }))}
        />
        <Select
          label={k('finalCheck.record')}
          data={recordOptions}
          value={recordId ?? null}
          readOnly={readOnly}
          clearable={!readOnly}
          searchable
          nothingFoundMessage={k('finalCheck.noRecords')}
          onChange={(value) => {
            const found = recordOptions.find((o) => o.value === value);
            setValue('data.checklist.formRecordId', value ?? null, { shouldDirty: true });
            setValue('data.checklist.formRecordLabel', found?.label ?? null, { shouldDirty: true });
          }}
          {...(readOnly ? { rightSection: null } : {})}
          classNames={{
            input: [noon.input, readOnly ? noon.locked : ''].join(' '),
            label: noon.label ?? '',
          }}
        />
      </div>
      {status === 'incomplete' || !readOnly ? (
        <NoteField
          name="data.checklist.remarks"
          label={k('finalCheck.checklistRemarks')}
          readOnly={readOnly}
          minRows={2}
        />
      ) : null}
      <MultiSelect
        label={k('finalCheck.defects')}
        data={defectOptions}
        value={items.map((i) => i.id)}
        readOnly={readOnly}
        searchable
        nothingFoundMessage={k('finalCheck.noDefects')}
        onChange={(ids) =>
          setValue(
            'data.openDefects.items',
            ids.map((id) => ({
              id,
              label: defectOptions.find((o) => o.value === id)?.label ?? id,
            })),
            { shouldDirty: true },
          )
        }
        classNames={{
          input: [noon.input, readOnly ? noon.locked : ''].join(' '),
          label: noon.label ?? '',
        }}
      />
      <NoteField
        name="data.openDefects.remarks"
        label={k('finalCheck.restrictions')}
        readOnly={readOnly}
        minRows={2}
      />
    </Group>
  );
}

/* ---------- Port Call bağı ve ATD/ATA önerisi ---------- */

export function PortCallSection({
  readOnly,
  context,
  suggestion,
}: {
  readOnly: boolean;
  context: VoyageReportContext | null;
  /** Gönderilmiş raporda: aramanın ATD/ATA'sı rapordan farklıysa uygulama önerisi (§7). */
  suggestion?: { text: string; onApply: (() => void) | null; busy?: boolean } | null;
}) {
  const k = useK();
  const { field } = useController({ name: 'data.portCall' });
  const value = (field.value ?? null) as { id: string; label: string } | null;
  if (readOnly && !value) return null;
  const options = (context?.portCalls ?? []).map((call) => ({
    value: call.id,
    label: `${call.portName}${call.berth ? ` – ${call.berth}` : ''} (ETA ${call.eta.slice(0, 10)})`,
  }));
  if (value && !options.some((o) => o.value === value.id)) {
    options.unshift({ value: value.id, label: value.label });
  }
  return (
    <Group title={k('portCall.title')} icon={<IconAnchor size={18} />} className={le.wide}>
      <Select
        label={k('portCall.call')}
        data={options}
        value={value?.id ?? null}
        readOnly={readOnly}
        clearable={!readOnly}
        searchable
        nothingFoundMessage={k('portCall.none')}
        onChange={(id) => {
          const found = options.find((o) => o.value === id);
          field.onChange(found ? { id: found.value, label: found.label } : null);
        }}
        {...(readOnly ? { rightSection: null } : {})}
        classNames={{
          input: [noon.input, readOnly ? noon.locked : ''].join(' '),
          label: noon.label ?? '',
        }}
      />
      {suggestion ? (
        <div className={noon.notice} data-tone="info">
          <IconInfoCircle size={16} aria-hidden />
          <span className={classes.suggestion}>
            {suggestion.text}
            {suggestion.onApply ? (
              <Button
                size="xs"
                variant="light"
                loading={suggestion.busy === true}
                onClick={suggestion.onApply}
              >
                {k('portCall.apply')}
              </Button>
            ) : null}
          </span>
        </div>
      ) : null}
    </Group>
  );
}

/* ---------- NOR ve halat (Berthing, EOSP, demir) ---------- */

export function NorSection({ readOnly }: { readOnly: boolean }) {
  const k = useK();
  if (useHidden(['data.nor'], readOnly)) return null;
  return (
    <Group title={k('nor.title')} icon={<IconFlag size={18} />} className={le.wide}>
      <div className={pairClass}>
        <LocalDateTimeField
          name="data.nor.tenderedAt"
          label={k('nor.tenderedAt')}
          readOnly={readOnly}
        />
        <TextField name="data.nor.tenderedTo" label={k('nor.tenderedTo')} readOnly={readOnly} />
        <LocalDateTimeField
          name="data.nor.acceptedAt"
          label={k('nor.acceptedAt')}
          readOnly={readOnly}
        />
        <TextField name="data.nor.acceptedBy" label={k('nor.acceptedBy')} readOnly={readOnly} />
        <LocalDateTimeField
          name="data.nor.laytimeCommencedAt"
          label={k('nor.laytime')}
          readOnly={readOnly}
        />
      </div>
      <NoteField name="data.nor.remarks" label={k('nor.remarks')} readOnly={readOnly} minRows={2} />
    </Group>
  );
}

export function MooringSection({ readOnly }: { readOnly: boolean }) {
  const k = useK();
  if (useHidden(['data.mooring'], readOnly)) return null;
  return (
    <Group title={k('mooring.title')}>
      <div className={pairClass}>
        <NumberField
          name="data.mooring.lines"
          label={k('mooring.lines')}
          decimals={0}
          readOnly={readOnly}
        />
        <NumberField
          name="data.mooring.shoreLines"
          label={k('mooring.shoreLines')}
          decimals={0}
          readOnly={readOnly}
        />
      </div>
      <NoteField
        name="data.mooring.remarks"
        label={k('mooring.remarks')}
        readOnly={readOnly}
        minRows={2}
      />
    </Group>
  );
}

/* ---------- EOSP: seyir incelemesi (COSP referansı, seyir özeti, CP) ---------- */

/**
 * EOSP formunda COSP → EOSP incelemesi (ADR 0058 §6; g4 §2, §4, §6): formdaki EOSP değerleri ve
 * seferin raporlarıyla canlı hesaplanır (`passageReview`); saklanmaz.
 */
export function PassageReviewPanel({
  reports,
  voyage,
  currentId,
}: {
  reports: readonly VoyageReport[];
  voyage: Voyage | null;
  currentId: string | null;
}) {
  const k = useK();
  const n = useNumber();
  const [reportedAt, rob, distance, freshWater] = useWatch({
    name: ['reportedAt', 'data.rob', 'data.distanceSinceLast', 'data.freshWater'],
  }) as [string, unknown, unknown, unknown];
  const offset = useWatch({ name: 'tzOffsetMin' }) as number;
  const review: PassageReview | null = reportedAt
    ? passageReview(
        reports.filter((r) => r.id !== currentId),
        { reportedAt, data: { rob, distanceSinceLast: distance, freshWater } },
        voyage ?? {},
      )
    : null;
  return (
    <Group title={k('passage.title')} icon={<IconRoute size={18} />} className={le.wide}>
      {review ? (
        <>
          <dl className={classes.facts}>
            <div>
              <dt>{k('passage.cosp')}</dt>
              <dd>
                {ltUtcText(review.cosp.reportedAt, offset ?? 0)}
                {review.cosp.lat !== null && review.cosp.lon !== null
                  ? ` · ${formatLatitude(review.cosp.lat)} ${formatLongitude(review.cosp.lon)}`
                  : ''}
              </dd>
            </div>
            <div>
              <dt>{k('passage.elapsed')}</dt>
              <dd>{n.unit(review.hours, 'h', { digits: 1, fixed: true })}</dd>
            </div>
            <div>
              <dt>{k('passage.distance')}</dt>
              <dd>{n.unit(review.distanceNm, 'NM', { digits: 1, fixed: true })}</dd>
            </div>
            <div>
              <dt>{k('passage.avgSpeed')}</dt>
              <dd>{n.unit(review.avgSpeedKn, 'kn', { digits: 2, fixed: true })}</dd>
            </div>
          </dl>
          {review.fuels.length > 0 ? (
            <table className={classes.mini}>
              <caption>{k('passage.fuel')}</caption>
              <thead>
                <tr>
                  <th scope="col">{k('rob.fuel')}</th>
                  <th scope="col">{k('passage.atCosp')}</th>
                  <th scope="col">{k('passage.atEosp')}</th>
                  <th scope="col">{k('passage.consumed')}</th>
                </tr>
              </thead>
              <tbody>
                {review.fuels.map((f) => (
                  <tr key={f.fuel}>
                    <th scope="row">{f.fuel}</th>
                    <td>{n(f.atCosp, { digits: 1, fixed: true })}</td>
                    <td>{n(f.atEosp, { digits: 1, fixed: true })}</td>
                    <td>{n(f.consumedMt, { digits: 1, fixed: true })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {review.cp ? (
            <dl className={classes.facts}>
              <div>
                <dt>{k('passage.cpSpeed', { load: review.cp.loadCondition })}</dt>
                <dd>
                  {n.unit(review.cp.orderedSpeedKn, 'kn', { digits: 1, fixed: true })}
                  {review.cp.speedDiffKn !== null
                    ? ` (${review.cp.speedDiffKn >= 0 ? '+' : ''}${n(review.cp.speedDiffKn, { digits: 2, fixed: true })})`
                    : ''}
                </dd>
              </div>
              <div>
                <dt>{k('passage.cpAllowed')}</dt>
                <dd>{n.unit(review.cp.allowedMt, 'MT', { digits: 1, fixed: true })}</dd>
              </div>
              <div>
                <dt>{k('passage.cpActual')}</dt>
                <dd>{n.unit(review.cp.actualMt, 'MT', { digits: 1, fixed: true })}</dd>
              </div>
              <div>
                <dt>{k('passage.cpDiff')}</dt>
                <dd data-tone={(review.cp.diffMt ?? 0) > 0 ? 'danger' : 'success'}>
                  {review.cp.diffMt === null
                    ? '—'
                    : `${review.cp.diffMt >= 0 ? '+' : ''}${n(review.cp.diffMt, { digits: 1, fixed: true })} MT (${n(review.cp.diffPct, { digits: 1, fixed: true })} %)`}
                </dd>
              </div>
            </dl>
          ) : null}
          <p className={noon.hint}>{k('passage.note')}</p>
        </>
      ) : (
        <p className={noon.muted}>{k('passage.noCosp')}</p>
      )}
    </Group>
  );
}

/* ---------- Berthing: EOSP referansı ---------- */

export function EospReferencePanel({
  reports,
  currentId,
}: {
  reports: readonly VoyageReport[];
  currentId: string | null;
}) {
  const k = useK();
  const n = useNumber();
  const reportedAt = useWatch({ name: 'reportedAt' }) as string | undefined;
  const at = reportedAt ? Date.parse(reportedAt) : Number.POSITIVE_INFINITY;
  const eosp = [...reports]
    .filter((r) => r.kind === 'eosp' && r.id !== currentId && Date.parse(r.reportedAt) <= at)
    .sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt))
    .at(-1);
  if (!eosp) return null;
  const rob = Array.isArray(eosp.data.rob) ? (eosp.data.rob as Record<string, unknown>[]) : [];
  return (
    <Group title={k('eospRef.title')} icon={<IconRoute size={18} />} className={le.wide}>
      <dl className={classes.facts}>
        <div>
          <dt>{k('eospRef.time')}</dt>
          <dd>{ltUtcText(eosp.reportedAt, eosp.tzOffsetMin)}</dd>
        </div>
        <div>
          <dt>{k('eospRef.position')}</dt>
          <dd>
            {eosp.lat !== null && eosp.lon !== null
              ? `${formatLatitude(eosp.lat)} ${formatLongitude(eosp.lon)}`
              : '—'}
          </dd>
        </div>
        <div>
          <dt>{k('eospRef.distanceToPort')}</dt>
          <dd>
            {n.unit(
              typeof eosp.data.distanceToPortNm === 'number' ? eosp.data.distanceToPortNm : null,
              'NM',
              { digits: 1, fixed: true },
            )}
          </dd>
        </div>
        <div>
          <dt>{k('eospRef.rob')}</dt>
          <dd>
            {rob.length > 0
              ? rob
                  .map((r) => `${String(r.fuel)} ${n(r.rob as number, { digits: 1, fixed: true })}`)
                  .join(' · ')
              : '—'}
          </dd>
        </div>
      </dl>
    </Group>
  );
}
